// ─── PublicAuditWidget.jsx ────────────────────────────────────────────────────
// Homepage lead-capture widget: URL → free audit preview → email → full PDF
import React, { useState } from "react";
import { API_BASE_URL } from "../config";
import { getPdfBase64 } from "./AuditPdfDocument";
import { useLanguage } from "./LanguageContext";
import { friendlyError } from "../lib/apiFetch";
import PageAuditReport from "./PageAuditReport";

// ─── Styles ───────────────────────────────────────────────────────────────────
const css = `
  .paw-root {
    font-family: 'DM Sans', sans-serif;
    width: 100%;
  }

  /* ── URL status indicator ── */
  .paw-url-status {
    font-size: 12px;
    margin-top: 6px;
    display: flex;
    align-items: center;
    gap: 5px;
  }
  .paw-url-status.checking { color: #64748b; }
  .paw-url-status.ok       { color: #16a34a; }
  .paw-url-status.fail     { color: #dc2626; }
  .paw-url-dot {
    width: 7px; height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .paw-url-status.checking .paw-url-dot { background: #94a3b8; animation: paw-pulse 1s infinite; }
  .paw-url-status.ok       .paw-url-dot { background: #16a34a; }
  .paw-url-status.fail     .paw-url-dot { background: #dc2626; }
  @keyframes paw-pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }

  /* ── Step 1: URL input ── */
  .paw-input-row {
    display: flex;
    gap: 8px;
    margin-bottom: 10px;
  }
  .paw-url-input {
    flex: 1;
    padding: 11px 14px;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    font-size: 14px;
    font-family: inherit;
    color: #0f172a;
    background: white;
    outline: none;
    transition: border-color 0.15s;
  }
  .paw-url-input:focus { border-color: #2563eb; }
  .paw-url-input::placeholder { color: #94a3b8; }
  .paw-url-input:disabled { background: #f8fafc; color: #94a3b8; }
  .paw-url-input.paw-url-input-error { border-color: #dc2626; }

  .paw-analyse-btn {
    padding: 11px 22px;
    border-radius: 8px;
    border: none;
    background: #2563eb;
    color: white;
    font-size: 14px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 7px;
    transition: background 0.15s, opacity 0.15s;
  }
  .paw-analyse-btn:hover:not(:disabled) { background: #1d4ed8; }
  .paw-analyse-btn:disabled { opacity: 0.6; cursor: not-allowed; }

  .paw-hint {
    font-size: 12px;
    color: #94a3b8;
    margin: 0;
  }
  .paw-error {
    font-size: 13px;
    color: #dc2626;
    margin-top: 8px;
    padding: 8px 12px;
    background: #fef2f2;
    border-radius: 6px;
    border: 1px solid #fecaca;
  }

  /* ── Progress ── */
  .paw-progress {
    padding: 24px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    text-align: center;
  }
  .paw-progress-title {
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
    margin: 0 0 16px;
  }
  .paw-progress-bar-wrap {
    background: #e2e8f0;
    border-radius: 99px;
    height: 6px;
    overflow: hidden;
    margin-bottom: 10px;
  }
  .paw-progress-bar {
    height: 100%;
    border-radius: 99px;
    background: linear-gradient(90deg, #2563eb, #7c3aed);
    transition: width 0.4s ease;
  }
  .paw-progress-step {
    font-size: 12.5px;
    color: #64748b;
  }

  /* ── Result preview (blurred gate) ── */
  .paw-result {
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
    background: white;
  }

  /* Header — always visible */
  .paw-result-header {
    padding: 20px 24px;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
  }
  .paw-result-url {
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
  }
  .paw-result-date {
    font-size: 12px;
    color: #94a3b8;
    margin-top: 2px;
  }
  .paw-score-badge {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    border-radius: 10px;
    font-weight: 800;
    font-size: 22px;
    min-width: 80px;
    justify-content: center;
  }
  .paw-score-label {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    opacity: 0.7;
    display: block;
  }

  /* Category pills — always visible */
  .paw-cats {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    padding: 16px 24px 20px;
    border-bottom: 1px solid #e2e8f0;
  }
  .paw-cat {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 10px 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .paw-cat-name {
    font-size: 12.5px;
    color: #475569;
    font-weight: 500;
  }
  .paw-cat-score {
    font-size: 16px;
    font-weight: 800;
  }
  .paw-cat-bar {
    height: 3px;
    border-radius: 99px;
    margin-top: 6px;
  }

  /* Blurred section */
  .paw-blurred-section {
    position: relative;
    overflow: hidden;
    min-height: 180px;
  }
  .paw-blurred-content {
    filter: blur(5px);
    pointer-events: none;
    user-select: none;
    padding: 16px 24px 100px;
  }
  .paw-blur-overlay {
    position: absolute;
    left: 0; right: 0; bottom: 0;
    height: 160px;
    background: linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.98) 35%);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    padding: 0 24px 24px;
  }
  .paw-blur-cta {
    text-align: center;
  }
  .paw-blur-title {
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 4px;
  }
  .paw-blur-sub {
    font-size: 13px;
    color: #64748b;
    margin: 0 0 16px;
  }

  /* ── Email gate ── */
  .paw-email-form {
    display: flex;
    gap: 8px;
    max-width: 420px;
    margin: 0 auto;
  }
  .paw-email-input {
    flex: 1;
    padding: 10px 14px;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    font-size: 13.5px;
    font-family: inherit;
    color: #0f172a;
    outline: none;
    transition: border-color 0.15s;
  }
  .paw-email-input:focus { border-color: #2563eb; }
  .paw-email-btn {
    padding: 10px 18px;
    border-radius: 8px;
    border: none;
    background: #2563eb;
    color: white;
    font-size: 13.5px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: background 0.15s;
  }
  .paw-email-btn:hover:not(:disabled) { background: #1d4ed8; }
  .paw-email-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  @media (max-width: 480px) {
    .paw-email-form {
      flex-direction: column;
      max-width: 100%;
    }
    .paw-email-btn {
      justify-content: center;
    }
  }
  .paw-disclaimer {
    font-size: 11px;
    color: #94a3b8;
    margin-top: 8px;
    text-align: center;
  }

  /* ── Success state ── */
  .paw-success {
    padding: 28px 24px;
    text-align: center;
    border-top: 1px solid #e2e8f0;
  }
  .paw-success-icon {
    font-size: 36px;
    margin-bottom: 10px;
  }
  .paw-success-title {
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 6px;
  }
  .paw-success-sub {
    font-size: 13px;
    color: #64748b;
    margin: 0;
  }

  /* ── Spinner ── */
  .paw-spinner {
    width: 16px; height: 16px;
    border: 2px solid rgba(255,255,255,0.35);
    border-top-color: white;
    border-radius: 50%;
    animation: paw-spin 0.7s linear infinite;
    flex-shrink: 0;
  }
  @keyframes paw-spin { to { transform: rotate(360deg); } }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const scoreColor  = (s) => s >= 75 ? "#16a34a" : s >= 50 ? "#f59e0b" : "#dc2626";
const scoreBg     = (s) => s >= 75 ? "#f0fdf4" : s >= 50 ? "#fffbeb" : "#fef2f2";
const fmtDate     = (iso) => iso ? new Date(iso).toLocaleDateString("de-CH") : "";

// ─── Main Component ───────────────────────────────────────────────────────────
export default function PublicAuditWidget() {
  const { t, lang } = useLanguage();
  const steps = [
    { pct: 15, label: t("public.widgetStep1") },
    { pct: 40, label: t("public.widgetStep2") },
    { pct: 70, label: t("public.widgetStep3") },
    { pct: 90, label: t("public.widgetStep4") },
  ];
  const catLabels = {
    contentQuality: t("public.widgetCatContent"),
    authority:      t("public.widgetCatAuthority"),
    technicalSeo:   t("public.widgetCatSeo"),
    engagement:     t("public.widgetCatEngagement"),
  };
  const [url,        setUrl]        = useState("");
  const [urlStatus,  setUrlStatus]  = useState(null); // null | checking | ok | fail
  const [checkTimer, setCheckTimer] = useState(null);
  const [phase,      setPhase]      = useState("idle"); // idle | loading | result | sending | done
  const [stepIdx,    setStepIdx]    = useState(0);
  const [progress,   setProgress]   = useState(0);
  const [auditData,  setAuditData]  = useState(null);
  const [email,      setEmail]      = useState("");
  const [error,      setError]      = useState("");
  const [sendError,  setSendError]  = useState("");

  // Store web-user context for report request
  const [webCtx, setWebCtx] = useState({ auditId: null, webUserId: null });

  // ── DNS reachability check (Cloudflare DoH — no CORS) ─────────────────────
  const checkUrlReachable = async (raw) => {
    try {
      const normalized = raw.trim().startsWith("http") ? raw.trim() : "https://" + raw.trim();
      const hostname = new URL(normalized).hostname;
      const res = await fetch(
        `https://cloudflare-dns.com/dns-query?name=${encodeURIComponent(hostname)}&type=A`,
        { headers: { Accept: "application/dns-json" }, signal: AbortSignal.timeout(3000) }
      );
      if (!res.ok) return true; // fail open
      const data = await res.json();
      return data.Status === 0 && Array.isArray(data.Answer) && data.Answer.length > 0;
    } catch {
      return true; // fail open if DNS check itself fails
    }
  };

  const handleUrlChange = (val) => {
    setUrl(val);
    setUrlStatus(null);
    if (checkTimer) clearTimeout(checkTimer);
    // Only check when it looks like a complete domain (has a dot)
    const clean = val.replace(/https?:\/\//, "").trim();
    if (clean.includes(".") && clean.length > 4) {
      setUrlStatus("checking");
      const timer = setTimeout(async () => {
        const ok = await checkUrlReachable(val);
        setUrlStatus(ok ? "ok" : "fail");
        if (ok) {
          const v = val.trim();
          if (v && !v.startsWith("http://") && !v.startsWith("https://")) setUrl("https://" + v);
        }
      }, 900);
      setCheckTimer(timer);
    }
  };

  // ── Step 1: Run audit ──────────────────────────────────────────────────────
  const handleAnalyse = async () => {
    if (!url.trim()) return;
    if (urlStatus === "fail") {
      setError(t("public.widgetErrUnreachable"));
      return;
    }
    setError("");

    // Normalize URL
    const finalUrl = url.trim().startsWith("http") ? url.trim() : "https://" + url.trim();
    setUrl(finalUrl);

    setPhase("loading");
    setProgress(5);
    setStepIdx(0);

    // Animate progress steps while waiting for API
    let si = 0;
    const ticker = setInterval(() => {
      si = Math.min(si + 1, steps.length - 1);
      setStepIdx(si);
      setProgress(steps[si].pct);
    }, 2200);

    try {
      const res  = await fetch(`${API_BASE_URL}/public-audit`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ url: finalUrl, lang }),
      });
      clearInterval(ticker);
      const data = await res.json();
      if (res.status === 429 && data.rateLimited) {
        setError(t("public.widgetRateLimit"));
        setPhase("idle");
        return;
      }
      if (!res.ok) throw new Error(data.error || t("public.widgetErrFailed"));
      setProgress(100);
      setWebCtx({ auditId: data.auditId, webUserId: data.webUserId });
      setAuditData(data);
      setTimeout(() => setPhase("result"), 400);
    } catch (err) {
      clearInterval(ticker);
      setError(friendlyError(err, lang));
      setPhase("idle");
    }
  };

  // ── Step 2: Send PDF to email ──────────────────────────────────────────────
  const handleRequestReport = async () => {
    if (!email.trim() || !auditData) return;
    setSendError("");
    setPhase("sending");

    try {
      // Generate PDF client-side, convert to base64
      const pdfBase64 = await getPdfBase64(auditData, lang);

      const res = await fetch(`${API_BASE_URL}/request-report`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          auditId:   webCtx.auditId,
          webUserId: webCtx.webUserId,
          email,
          pdfBase64,
          url: auditData.url,
          lang,
        }),
      });
      const data = await res.json();
      if (!res.ok) { const e = new Error(data.error || ""); e.status = res.status; throw e; }
      setPhase("done");
    } catch (err) {
      setSendError(friendlyError(err, lang));
      setPhase("result");
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="paw-root">
      <style>{css}</style>

      {/* URL Input — always shown */}
      <div className="paw-input-row">
        <input
          className={`paw-url-input${urlStatus === "fail" ? " paw-url-input-error" : ""}`}
          type="text"
          placeholder={t("public.widgetPlaceholder")}
          value={url}
          onChange={e => handleUrlChange(e.target.value)}
          onKeyDown={e => e.key === "Enter" && (phase === "idle" || phase === "result") && handleAnalyse()}
          disabled={phase !== "idle" && phase !== "result"}
        />
        <button
          className="paw-analyse-btn"
          onClick={handleAnalyse}
          disabled={phase === "loading" || phase === "sending" || !url.trim()}
        >
          {phase === "loading" ? (
            <><div className="paw-spinner" /> {t("public.widgetBtnAnalysing")}</>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
              {t("public.widgetBtnAnalyse")}
            </>
          )}
        </button>
      </div>
      {urlStatus && (
        <div className={`paw-url-status ${urlStatus}`}>
          <span className="paw-url-dot" />
          {urlStatus === "checking" && t("public.widgetStatusChecking")}
          {urlStatus === "ok"       && t("public.widgetStatusOk")}
          {urlStatus === "fail"     && t("public.widgetStatusFail")}
        </div>
      )}
      {!urlStatus && <p className="paw-hint">{t("public.widgetHint")}</p>}
      {error && <div className="paw-error">⚠ {error}</div>}

      {/* Progress */}
      {phase === "loading" && (
        <div className="paw-progress" style={{marginTop:16}}>
          <p className="paw-progress-title">{t("public.widgetProgressTitle")}</p>
          <div className="paw-progress-bar-wrap">
            <div className="paw-progress-bar" style={{width: `${progress}%`}} />
          </div>
          <p className="paw-progress-step">{steps[stepIdx].label}</p>
        </div>
      )}

      {/* Result */}
      {(phase === "result" || phase === "sending" || phase === "done") && auditData && (
        <div className="paw-result" style={{marginTop:16}}>

          {/* Header — always visible */}
          <div className="paw-result-header">
            <div>
              <div className="paw-result-url">
                {auditData.url?.replace(/https?:\/\//, "")}
              </div>
              <div className="paw-result-date">{fmtDate(auditData.auditDate)}</div>
            </div>
            <div className="paw-score-badge" style={{
              background: scoreBg(auditData.overallScore),
              color: scoreColor(auditData.overallScore),
              border: `1.5px solid ${scoreColor(auditData.overallScore)}22`
            }}>
              <div>
                <span className="paw-score-label">Score</span>
                {auditData.overallScore}
              </div>
            </div>
          </div>

          {/* Category scores — always visible */}
          <div className="paw-cats">
            {Object.entries(auditData.categories || {}).map(([key, cat]) => (
              <div className="paw-cat" key={key}>
                <div style={{flex:1}}>
                  <div className="paw-cat-name">{catLabels[key] || key}</div>
                  <div className="paw-cat-bar" style={{
                    width: `${cat.score}%`,
                    background: scoreColor(cat.score),
                    maxWidth: "100%"
                  }} />
                </div>
                <div className="paw-cat-score" style={{color: scoreColor(cat.score), marginLeft:10}}>
                  {cat.score}
                </div>
              </div>
            ))}
          </div>

          {/* Summary — always visible */}
          <div style={{padding:"16px 24px", borderBottom:"1px solid #e2e8f0"}}>
            <p style={{fontSize:13.5, color:"#475569", lineHeight:1.7, margin:0}}>
              {auditData.summary}
            </p>
          </div>

          {/* Blurred detail + email gate or success */}
          {phase !== "done" ? (
            <div className="paw-blurred-section">
              <div className="paw-blurred-content">
                <p style={{fontSize:13, color:"#64748b", lineHeight:1.6, margin:"0 0 10px"}}>
                  {t("public.widgetBlurredText")}
                </p>
                <div style={{height:60}} />
              </div>
              <div className="paw-blur-overlay">
                <div className="paw-blur-cta">
                  <p className="paw-blur-title">{t("public.widgetBlurTitle")}</p>
                  <p className="paw-blur-sub">{t("public.widgetBlurSub")}</p>
                  <div className="paw-email-form">
                    <input
                      className="paw-email-input"
                      type="email"
                      placeholder={t("public.widgetEmailPlaceholder")}
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && handleRequestReport()}
                      disabled={phase === "sending"}
                    />
                    <button
                      className="paw-email-btn"
                      onClick={handleRequestReport}
                      disabled={phase === "sending" || !email.trim()}
                    >
                      {phase === "sending"
                        ? <><div className="paw-spinner" /> {t("public.widgetBtnSending")}</>
                        : <>{t("public.widgetBtnSend")}</>
                      }
                    </button>
                  </div>
                  {sendError && (
                    <p style={{fontSize:12, color:"#dc2626", marginTop:8}}>{sendError}</p>
                  )}
                  <p className="paw-disclaimer">
                    {t("public.widgetDisclaimer")}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="paw-success">
              <div className="paw-success-icon">✅</div>
              <p className="paw-success-title">{t("public.widgetSuccessTitle")}</p>
              <p className="paw-success-sub">
                {t("public.widgetSuccessSub")} <strong>{email}</strong>.
              </p>
            </div>
          )}

        </div>
      )}

      {/* Full interactive report — unlocked after email capture */}
      {phase === "done" && auditData && (
        <div style={{ marginTop: 24, border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
          <PageAuditReport data={auditData} lang={lang} />
        </div>
      )}
    </div>
  );
}
